/**
 * structuredLogger — A small leveled logger with optional JSON output.
 *
 * Demos sprinkle `console.log` / `console.error` at panic-time, debug
 * messages, and CLI-script output without distinguishing them.
 * `structuredLogger` lets the demo declare intent — "this is an error",
 * "this is a debug breadcrumb" — and pick a sink at construction time:
 *
 *   - **pretty** (default): one human-readable line per record, sent
 *     to the matching `console.*` method;
 *   - **json**: a single JSON line per record, sent to `console.log`,
 *     so CI / tooling consumers can parse the output without scraping;
 *   - **silent**: drops every record. Useful in tests.
 *
 * The logger holds no state and never throws, even if the structured
 * payload contains values that resist serialization — the formatter
 * falls back to `String(value)` for any field whose `JSON.stringify`
 * raises.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogRecord {
  readonly level: LogLevel;
  readonly message: string;
  readonly context?: Readonly<Record<string, unknown>>;
}

export interface StructuredLogger {
  readonly debug: (message: string, context?: Readonly<Record<string, unknown>>) => void;
  readonly info: (message: string, context?: Readonly<Record<string, unknown>>) => void;
  readonly warn: (message: string, context?: Readonly<Record<string, unknown>>) => void;
  readonly error: (message: string, context?: Readonly<Record<string, unknown>>) => void;
  /** Lower-level entry-point for tooling that already has a {@link LogRecord}. */
  readonly emit: (record: LogRecord) => void;
}

export type LogSink = "pretty" | "json" | "silent";

export interface StructuredLoggerOptions {
  /** Output format. Defaults to `"pretty"`. */
  readonly sink?: LogSink;
  /** Minimum level to emit. Records below the threshold are dropped. */
  readonly minLevel?: LogLevel;
  /** Tag prefix prepended to every pretty-format record. */
  readonly tag?: string;
  /** Override the console reference (mostly for tests). */
  readonly console?: Pick<Console, "debug" | "info" | "warn" | "error" | "log">;
}

const LEVEL_RANK: Readonly<Record<LogLevel, number>> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * A `JSON.stringify` replacer that turns values which JSON cannot
 * represent — cycles, BigInt, functions, symbols, undefined — into
 * tagged strings so the output always parses. Used by the `json` sink
 * to preserve its machine-parseable contract even when the caller
 * passes a payload that would otherwise raise `TypeError`.
 *
 * The cycle guard uses a `WeakSet` of objects seen along the current
 * branch; references re-encountered at the same depth are replaced
 * with `"[Circular]"`. Tagged strings (`"[BigInt 42]"`,
 * `"[Function foo]"`, `"[Symbol(x)]"`) preserve enough information to
 * be intelligible in a log without leaking object internals.
 */
function jsonSafeReplacer(): (key: string, value: unknown) => unknown {
  const seen = new WeakSet();
  return (_key, value) => {
    if (typeof value === "bigint") return `[BigInt ${value.toString()}]`;
    if (typeof value === "function") return `[Function ${value.name || "anonymous"}]`;
    if (typeof value === "symbol") return `[${value.toString()}]`;
    if (typeof value === "undefined") return "[undefined]";
    if (value !== null && typeof value === "object") {
      if (seen.has(value)) return "[Circular]";
      seen.add(value);
    }
    return value;
  };
}

/** Serialize for the `json` sink — always returns a parseable JSON string. */
function jsonSafeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, jsonSafeReplacer());
  } catch (error) {
    // Defence in depth: even with the replacer, a sufficiently exotic
    // value (e.g. a getter that throws) could still fail. Emit a
    // single-key object so the caller still gets a parseable line.
    return JSON.stringify({
      __structuredLoggerError: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Lossy serializer used by the `pretty` sink. We keep the unsafe
 * fallback to `String(value)` here because human-facing logs benefit
 * from a `[object Object]` breadcrumb when the payload is truly
 * unprintable; only the `json` sink has to guarantee parseability.
 */
function prettyStringify(value: unknown): string {
  try {
    return JSON.stringify(value, jsonSafeReplacer());
  } catch {
    return String(value);
  }
}

function formatPretty(record: LogRecord, tag: string | undefined): string {
  const prefix = tag !== undefined ? `[${tag}] ` : "";
  const upper = record.level.toUpperCase();
  if (record.context === undefined || Object.keys(record.context).length === 0) {
    return `${prefix}${upper} ${record.message}`;
  }
  return `${prefix}${upper} ${record.message} ${prettyStringify(record.context)}`;
}

function formatJson(record: LogRecord, tag: string | undefined): string {
  const payload: Record<string, unknown> = {
    timestamp: new Date().toISOString(),
    level: record.level,
    message: record.message,
  };
  if (tag !== undefined) payload["tag"] = tag;
  if (record.context !== undefined) payload["context"] = record.context;
  return jsonSafeStringify(payload);
}

/** Build a logger backed by the given sink. */
export function createStructuredLogger(options: StructuredLoggerOptions = {}): StructuredLogger {
  const { sink = "pretty", minLevel = "debug", tag, console: consoleRef = console } = options;
  const minRank = LEVEL_RANK[minLevel];

  const emit = (record: LogRecord): void => {
    if (LEVEL_RANK[record.level] < minRank) return;
    if (sink === "silent") return;
    if (sink === "json") {
      consoleRef.log(formatJson(record, tag));
      return;
    }
    const line = formatPretty(record, tag);
    switch (record.level) {
      case "debug":
        consoleRef.debug(line);
        return;
      case "info":
        consoleRef.info(line);
        return;
      case "warn":
        consoleRef.warn(line);
        return;
      case "error":
        consoleRef.error(line);
        return;
    }
  };

  const at =
    (level: LogLevel) =>
    (message: string, context?: Readonly<Record<string, unknown>>): void => {
      emit(context === undefined ? { level, message } : { level, message, context });
    };

  return {
    debug: at("debug"),
    info: at("info"),
    warn: at("warn"),
    error: at("error"),
    emit,
  };
}
